import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import DragDropColor from "../../Components/DragDropColors/DragDropColor";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import Toast from "../../Components/Common/Toast";
import DeleteConfirmation from "../../Components/Common/DeleteConfirmation";
import { setDropDown } from "../../redux/lessonsSlice";
import { setParticles } from "../../redux/particlesReducerSlice";
import { useDispatch } from "react-redux";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import ShimmerEffect from "../../utils/ShimmerEffect";
import { useResponsiveFlex } from "../../utils/useResponsiveFlex";
import { useDebounce } from "../../hooks/useDebounce";
import copyIcon from "../../assets/copyIcon.svg";

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

interface DragDropColorsList {
  id: number;
  sentence: string;
  themeName: string;
  themeIsDeleted: boolean;
  lessonLevel: string;
  points: string;
  updatedAt: string;
  active: boolean;
}

const DragDropColorsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dragDropColors, setDragDropColors] = useState<DragDropColorsList[]>(
    []
  );
  const [selectedItem, setSelectedItem] = useState<string | null>(searchParams.get("filter") || null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(searchParams.get("level") || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedColors, setSelectedColors] = useState<number[]>([]);
  const [selectedColorsId, setSelectedColorsId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const flex = useResponsiveFlex();

  const getFiltersFromItem = (item: string | null): { level?: string; updatedAt?: number; active?: string } => {
    switch (item) {
      case "Latest update": return { updatedAt: -1 };
      case "Active": return { active: "true" };
      case "InActive": return { active: "false" };
      default: return {};
    }
  };

  const buildParams = (p: number, l: number, s: string, f: string | null, lv: string | null): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    if (f && f !== "All") params.filter = f;
    if (lv && lv !== "All Levels") params.level = lv;
    return params;
  };

  const [filters, setFilters] = useState<{
    level?: string;
    updatedAt?: number;
    active?: string;
  }>(() => {
    const init = getFiltersFromItem(searchParams.get("filter"));
    const urlLevel = searchParams.get("level");
    if (urlLevel && urlLevel !== "All Levels") init.level = urlLevel;
    return init;
  });

  const handleSelect = (item: string) => {
    setSelectedItem(item);
  };

  const handleLevelSelect = (item: string) => {
    setSelectedLevel(item);
  };

  const dropdownItems: string[] = [
    "All",
    "Active",
    "InActive",
    "Latest update",
  ];

  const levelDropdownItems: string[] = [
    "All Levels",
    "A1",
    "A2",
    "B1",
    "B2",
  ];

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    const urlFilter = searchParams.get("filter") || null;
    const urlLevel = searchParams.get("level") || null;
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
    setSelectedItem(urlFilter);
    setSelectedLevel(urlLevel);
    const synced = getFiltersFromItem(urlFilter);
    if (urlLevel && urlLevel !== "All Levels") synced.level = urlLevel;
    setFilters(synced);
  }, [searchParams]);

  useEffect(() => {
    const updatedFilters = {
      ...filters,
      search: debouncedSearch,
    };
    fetchColors(page + 1, limit, updatedFilters);
  }, [debouncedSearch, page, limit, filters]);

  //fetch Colors list
  const fetchColors = async (
    page: number,
    perPage: number,
    filters: {
      level?: string;
      updatedAt?: number;
      active?: string;
      search?: string;
    }
  ) => {
    if (!filters) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }

    try {
      let url = `${baseURL}/drag-and-drop-colors-challenge/get-all-drag-and-drop-colors-challenge`;
      const body = {
        page,
        limit: perPage,
        ...filters,
      };
      const response = await postRequest(url, body);
      setLoading(false);
      setFilterLoading(false);
      if (response?.data) {
        setDragDropColors(response.data.data.result);
        setCount(response.data.data.totalChallengesCount);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage);
        setHasPreviousPage(response.data.data.hasPreviousPage);
      }

      const fetchLessons = await getRequest(
        `${baseURL}/notification-challenge/get-lesson-dropdown`
      );

      const fetchParticles = await getRequest(
        `${baseURL}/particle/get-dropdown`
      );
      dispatch(setDropDown(fetchLessons?.data?.data || []));
      dispatch(setParticles(fetchParticles?.data?.data || []));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query, selectedItem, selectedLevel));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery, selectedItem, selectedLevel));
  };

  const handleApplyFilters = () => {
    const newFilters = getFiltersFromItem(selectedItem);
    if (selectedLevel && selectedLevel !== "All Levels") {
      newFilters.level = selectedLevel;
    }
    setFilters(newFilters);
    setPage(0);
    setSearchParams(buildParams(0, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleEditColors = (id: number) => {
    navigate(`/drag-drop-colors/edit-drag-drop-colors/${id}`, {
      state: { listPage: page, listLimit: limit, listSearch: searchQuery, listFilter: selectedItem, listLevel: selectedLevel },
    });
  };

  const handleCopyDragDropColor = async (id: number) => {
    try {
      const response = await postRequest(
        `${baseURL}/drag-and-drop-colors-challenge/clone-drag-and-drop-colors-challenge`,
        { id }
      );
      if (response?.data.success) {
        Toast(
          response.data.statusMessage || "Drag & drop color cloned successfully",
          "success"
        );
        const updatedFilters = { ...filters, search: debouncedSearch };
        fetchColors(page + 1, limit, updatedFilters);
      } else {
        Toast("Failed to clone drag & drop color", "error");
      }
    } catch (error) {
      console.error("Error cloning drag & drop color:", error);
      Toast("Failed to clone drag & drop color", "error");
    }
  };

  /** Bulk copy all selected drag & drop colors — clones each one server-side */
  const handleCopySelectedColors = async () => {
    if (selectedColors.length === 0) return;
    try {
      for (const id of selectedColors) {
        await postRequest(
          `${baseURL}/drag-and-drop-colors-challenge/clone-drag-and-drop-colors-challenge`,
          { id }
        );
      }
      Toast(`${selectedColors.length} drag & drop color(s) copied successfully`, "success");
      setSelectedColors([]);
      const updatedFilters = { ...filters, search: debouncedSearch };
      fetchColors(page + 1, limit, updatedFilters);
    } catch (error) {
      console.error("Error copying drag & drop colors:", error);
      Toast("Failed to copy drag & drop colors", "error");
    }
  };

  const handleCreatedragDropColors = () => {
    navigate("/drag-drop-colors/create-drag-drop-colors");
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const reqData = {
      modelName: "dragAndDropColorsChallenge",
      id: id,
      isActive: !active,
    };
    await postRequest(`${baseURL}/change-status`, reqData);
    // await fetchColors(page + 1, limit, filters);
  };

  const onCheckboxChange = (id: number) => {
    if (selectedColors.includes(id)) {
      setSelectedColors(
        selectedColors.filter((selectedId) => selectedId !== id)
      );
    } else {
      setSelectedColors([...selectedColors, id]);
    }
  };

  const deleteSelectedColors = () => {
    if (selectedColors.length > 0) {
      setShowDeleteDialog(true);
    } else {
      Toast("Please select at least one challenge to delete.", "error");
    }
  };

  const deleteColors = async (colorsIds: number | number[]) => {
    try {
      const payload = {
        id: Array.isArray(colorsIds) ? colorsIds : colorsIds,
      };
      const response = await postRequest(
        `${baseURL}/drag-and-drop-colors-challenge/delete-drag-and-drop-colors-challenge-by-id`,
        payload
      );

      if (response?.status === 200) {
        Toast("Colors deleted successfully", "success");
        setDragDropColors((prevData) =>
          prevData.filter((colors) =>
            Array.isArray(colorsIds)
              ? !colorsIds.includes(colors.id)
              : colors.id !== colorsIds
          )
        );
      } else {
        Toast("Failed to delete challenge", "error");
      }
    } catch (error) {
      console.error("Error deleting challenge:", error);
    }
  };

  const handleDelete = async () => {
    if (selectedColorsId !== null) {
      await deleteColors(selectedColorsId);
    } else {
      await deleteColors(selectedColors);
    }
    // await fetchColors(page + 1, limit, filters);
    setSelectedColors([]);
    setSelectedColorsId(null);
    setShowDeleteDialog(false);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [loading]);

  return (
    <>
      {" "}
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>
                    Challenges drag & drop colors
                  </h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Buttons Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={dropdownItems}
                    onSelect={handleSelect}
                    selectedItem={selectedItem || ""}
                    height="50px"
                  />
                  <div className="ms-3">
                    <CustomDropdown
                      items={levelDropdownItems}
                      onSelect={handleLevelSelect}
                      selectedItem={selectedLevel || ""}
                      height="50px"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary ms-3 user"
                    style={{
                      backgroundColor: "#100F15",
                      borderRadius: "10px",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 50px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
                <div className="col d-flex justify-content-end responsive">
                  {/* <button
                    type="button"
                    className="btn btn-outline-secondary me-2 text-center"
                    style={{
                      color: "#100F15",
                      background: "none",
                      border: "1px solid #100F15",
                      borderRadius: "10px",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={deleteSelectedColors}
                  >
                    Delete all
                  </button> */}
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleCreatedragDropColors}
                  >
                    Add New
                  </button>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      justifyContent="space-between"
                      className="row-container"
                    >
                      {/* Select-all checkbox for current page */}
                      <RowContainer flex="1.4" alignItem="center">
                        <input
                          type="checkbox"
                          title="Select all on this page"
                          checked={
                            dragDropColors.length > 0 &&
                            dragDropColors.every((c) =>
                              selectedColors.includes(c.id)
                            )
                          }
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedColors.length > 0 &&
                                !dragDropColors.every((c) =>
                                  selectedColors.includes(c.id)
                                );
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const currentPageIds = dragDropColors.map((c) => c.id);
                              setSelectedColors((prev) => [
                                ...prev.filter((id) => !currentPageIds.includes(id)),
                                ...currentPageIds,
                              ]);
                            } else {
                              const currentPageIds = dragDropColors.map((c) => c.id);
                              setSelectedColors((prev) =>
                                prev.filter((id) => !currentPageIds.includes(id))
                              );
                            }
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: "#7542E4",
                            cursor: "pointer",
                          }}
                        />
                      </RowContainer>
                      <RowContainer flex={flex.name}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.theme}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Theme
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3.1">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.point}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Point
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.action} justifyContent="space-between" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Action
                        </Label>
                        {/* Bulk Copy toolbar — visible only when colors are selected */}
                        {selectedColors.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn user"
                              title={`Copy ${selectedColors.length} selected color(s)`}
                              style={{
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                                whiteSpace: "nowrap",
                              }}
                              onClick={handleCopySelectedColors}
                            >
                              <img
                                src={copyIcon}
                                alt="copy"
                                style={{ width: 16, height: 16 }}
                              />
                              Copy ({selectedColors.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title="Clear selection"
                              style={{
                                background: "none",
                                border: "1px solid #aaa",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 10px",
                                color: "#555",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => setSelectedColors([])}
                            >
                              ✕ Clear
                            </button>
                          </div>
                        )}
                      </RowContainer>
                    </RowContainer>
                    {filterLoading ? (
                      <>
                        <RowContainer
                          bgColor="#ffffff"
                          borderRadius="8px"
                          padding="10px 0px"
                        >
                          {Array.from({ length: 6 }, (_, index) => {
                            return (
                              <RowContainer
                                key={index}
                                width={
                                  index === 0 || index === 5 ? "10%" : "20%"
                                }
                                justifyContent="center"
                              >
                                <ShimmerEffect />
                              </RowContainer>
                            );
                          })}
                        </RowContainer>
                      </>
                    ) : (
                      <>
                        {dragDropColors && dragDropColors.length > 0 ? (
                          <ColumnContainer>
                            {dragDropColors.map((dragDropColor) => (
                              <DragDropColor
                                key={dragDropColor.id}
                                id={dragDropColor.id}
                                sentence={dragDropColor.sentence || "N/A"}
                                theme={dragDropColor.themeName}
                                themeIsDeleted={dragDropColor.themeIsDeleted}
                                lessonLevel={dragDropColor.lessonLevel}
                                point={`${dragDropColor.points} pts`}
                                created={formatDate(dragDropColor.updatedAt)}
                                onEdit={handleEditColors}
                                onDelete={() => {
                                  setSelectedColorsId(dragDropColor.id);
                                  toggleDeleteDialog();
                                }}
                                isChecked={selectedColors.includes(
                                  dragDropColor.id
                                )}
                                onCheckboxChange={onCheckboxChange}
                                status={dragDropColor.active}
                                onStatusChange={() =>
                                  handleStatusChange(
                                    dragDropColor.id,
                                    dragDropColor.active
                                  )
                                }
                                onCopy={handleCopyDragDropColor}
                              />
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No results found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            {showDeleteDialog && (
              <DeleteConfirmation
                showDeletePopup={true}
                setShowDeletePopup={setShowDeleteDialog}
                message={`Are you sure you want to delete ${selectedColors.length === 0
                  ? "this drag & drop colors?"
                  : selectedColors.length === 1
                    ? "this drag & drop colors?"
                    : "these drag & drop colors?"
                  }`}
                deleteButtonLabel="Delete"
                onDelete={handleDelete}
              />
            )}
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default DragDropColorsList;
